import React from "react";

/**
 * Brutalist body-measurements infographic for the Home hero.
 * Reads from the active profile when available; otherwise shows sample values.
 *
 * @param {object} props.profile  Optional profile with measurements
 */
export const BodyMeasurementsCard = ({ profile }) => {
    const m = profile?.measurements || {};

    const chest = m.chest_in ? `${Number(m.chest_in).toFixed(1)}"` : "38.0\"";
    const waist = m.waist_in ? `${Number(m.waist_in).toFixed(1)}"` : "30.0\"";
    const hips = m.hips_in ? `${Number(m.hips_in).toFixed(1)}"` : "38.0\"";
    const inseam = m.inseam_in ? `${Number(m.inseam_in).toFixed(1)}"` : "32.0\"";
    const shoe = m.shoe_size_us ? `${m.shoe_size_us}` : "9";
    const height = m.height_cm ? `${Math.round(m.height_cm)} CM` : "170 CM";

    const isReal = !!profile?.measurements?.chest_in;

    return (
        <div
            data-testid="body-measurements-card"
            className="border-4 border-black bg-[#00E5FF] shadow-brutal-lg overflow-hidden relative flex flex-col"
        >
            {/* Header */}
            <div className="px-5 pt-5 pb-3 border-b-2 border-black flex items-start justify-between gap-3 bg-[#00E5FF]">
                <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-black/70">
                        / {isReal ? "Your" : "Sample"} measurements · Your fit /
                    </p>
                    <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter leading-[0.95] mt-1">
                        BUILT AROUND
                        <br />
                        <span className="bg-black text-[#CCFF00] px-1.5">YOUR BODY</span>
                    </h3>
                </div>
                <div className="text-right shrink-0">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-black/60">Height</p>
                    <p className="font-display text-lg font-black leading-none">{height}</p>
                </div>
            </div>

            {/* Diagram */}
            <div className="relative flex-1 px-3 py-4 grid grid-cols-[1fr_220px_1fr] gap-2 items-stretch min-h-[340px]">
                {/* Left column — Chest + Waist */}
                <div className="flex flex-col justify-around py-3 gap-3">
                    <MeasureChip label="Chest" value={chest} accent="left" />
                    <MeasureChip label="Waist" value={waist} accent="left" />
                </div>

                {/* Body photos — athleisure pair (womens + mens) */}
                <div className="relative flex items-stretch justify-center">
                    <div className="grid grid-cols-2 gap-2 w-full">
                        {/* Womens reference w/ measurement overlays */}
                        <div className="relative border-2 border-black overflow-hidden bg-black">
                            <img
                                src="https://images.unsplash.com/photo-1596641211273-938aeaf926a9?crop=entropy&cs=srgb&fm=jpg&w=600&q=85"
                                alt="Womens athleisure body reference"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ objectPosition: "50% 25%" }}
                            />
                            {/* Halftone grain overlay for brutalist texture */}
                            <div
                                className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-40"
                                style={{
                                    backgroundImage:
                                        "repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 4px)",
                                }}
                            />

                            {/* Connector dashed lines from each label to the body */}
                            <span className="absolute left-0 right-1/2 border-t-2 border-dashed border-black/80" style={{ top: "22%" }} />
                            <span className="absolute left-0 right-1/2 border-t-2 border-dashed border-black/80" style={{ top: "42%" }} />
                            <span className="absolute left-1/2 right-0 border-t-2 border-dashed border-black/80" style={{ top: "55%" }} />
                            <span className="absolute left-1/2 right-0 border-t-2 border-dashed border-black/80" style={{ top: "82%" }} />

                            {/* Anchor dots */}
                            <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-[#CCFF00] border-2 border-black rounded-full" style={{ left: "50%", top: "22%" }} />
                            <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-[#CCFF00] border-2 border-black rounded-full" style={{ left: "50%", top: "42%" }} />
                            <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-[#CCFF00] border-2 border-black rounded-full" style={{ left: "50%", top: "55%" }} />
                            <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-[#CCFF00] border-2 border-black rounded-full" style={{ left: "50%", top: "82%" }} />

                            {/* Fuchsia measuring-tape band across the waist */}
                            <div
                                className="absolute left-0 right-0 bg-[#FF00FF] border-y-2 border-black flex items-center justify-center font-mono text-[9px] font-bold tracking-widest uppercase text-black"
                                style={{ top: "calc(42% - 9px)", height: "18px" }}
                            >
                                {waist.replace('"', " IN")}
                            </div>

                            {/* Persona stamp */}
                            <span className="absolute bottom-1.5 left-1.5 bg-[#CCFF00] border-2 border-black px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-widest">
                                W
                            </span>
                        </div>

                        {/* Mens reference — beachside muscle-beach workout */}
                        <div className="relative border-2 border-black overflow-hidden bg-black">
                            <img
                                src="https://images.unsplash.com/photo-1526314149856-d8cf115d62f1?crop=entropy&cs=srgb&fm=jpg&w=600&q=85"
                                alt="Mens athleisure body reference — beachside outdoor gym"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ objectPosition: "50% 35%" }}
                            />
                            {/* Halftone grain overlay */}
                            <div
                                className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-40"
                                style={{
                                    backgroundImage:
                                        "repeating-linear-gradient(-45deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 4px)",
                                }}
                            />

                            {/* Persona stamp */}
                            <span className="absolute bottom-1.5 left-1.5 bg-[#FF00FF] border-2 border-black px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-widest">
                                M
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right column — Hips + Inseam */}
                <div className="flex flex-col justify-around py-3 gap-3 items-end">
                    <MeasureChip label="Hips" value={hips} accent="right" />
                    <MeasureChip label="Inseam" value={inseam} accent="right" />
                </div>
            </div>

            {/* Footer */}
            <div className="border-t-2 border-black bg-black text-[#CCFF00] px-5 py-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest gap-3">
                <span>/ Shoe: <span className="text-white font-bold">{shoe}</span> /</span>
                <span className="truncate">{isReal ? "Routing your size across 8 stores" : "Build profile → see your size"}</span>
            </div>
        </div>
    );
};

const MeasureChip = ({ label, value, accent }) => (
    <div
        data-testid={`measure-${label.toLowerCase()}`}
        className={`bg-white border-2 border-black px-3 py-2 shadow-brutal-sm inline-block ${
            accent === "right" ? "text-right" : "text-left"
        }`}
    >
        <p className="font-mono text-[9px] uppercase tracking-widest text-black/60 leading-none">
            {label}
        </p>
        <p className="font-display text-lg md:text-xl font-black leading-tight">
            {value}
            <span className="font-mono text-[9px] uppercase tracking-widest text-black/50 ml-1">
                {label === "Shoe" ? "US" : "in"}
            </span>
        </p>
    </div>
);
